class BlakoCartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount"]');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[data-discount-status]');

    if (this.form && this.form.dataset.bound !== 'true') {
      this.form.dataset.bound = 'true';
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.querySelectorAll('[data-remove-discount]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', this.onRemove.bind(this));
    });
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('is-error', isError);
  }

  setApplyLoading(isLoading) {
    if (!this.button || !this.input) return;
    this.button.disabled = isLoading;
    this.input.disabled = isLoading;
    this.button.textContent = isLoading ? 'APPLYING' : 'APPLY';
  }

  getApplications(cart) {
    const groups = [
      cart.discount_applications,
      cart.cart_level_discount_applications,
      cart.discount_codes,
      cart.discountCodes,
    ];

    return groups.flatMap((group) => (Array.isArray(group) ? group : []));
  }

  isCodeApplied(cart, code) {
    const normalizedCode = String(code || '').trim().toLocaleLowerCase();
    if (!normalizedCode) return false;

    const directMatch = this.getApplications(cart).some((application) => {
      const title = application.code || application.title || '';
      return String(title).trim().toLocaleLowerCase() === normalizedCode && application.applicable !== false;
    });

    if (directMatch) return true;

    return Array.isArray(cart.items)
      ? cart.items.some((item) => {
          const allocations = Array.isArray(item.line_level_discount_allocations)
            ? item.line_level_discount_allocations
            : Array.isArray(item.discounts)
              ? item.discounts
              : [];

          return allocations.some((allocation) => {
            const title = allocation.discount_application?.title || allocation.title || '';
            return String(title).trim().toLocaleLowerCase() === normalizedCode;
          });
        })
      : false;
  }

  async updateDiscounts(discountValue) {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: discountValue,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });

    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart.description || cart.message || 'The promo code could not be updated.');
    }

    return cart;
  }

  renderCart(cart, { openPromo = false, inputValue = '', message = '', isError = false } = {}) {
    const drawer = document.querySelector('cart-drawer');
    const canRenderDrawer = cart.sections?.['cart-drawer'] && cart.sections?.['cart-icon-bubble'];

    if (!drawer || typeof drawer.renderContents !== 'function' || !canRenderDrawer) {
      window.location.reload();
      return;
    }

    drawer.renderContents(cart);

    const refreshedDiscount = document.querySelector('blako-cart-discount');
    const refreshedDetails = refreshedDiscount?.querySelector('.blako-cart-discount details');
    const refreshedInput = refreshedDiscount?.querySelector('input[name="discount"]');

    if (openPromo && refreshedDetails) refreshedDetails.open = true;
    if (refreshedInput) refreshedInput.value = inputValue;
    if (message) refreshedDiscount?.setStatus(message, isError);
  }

  async onSubmit(event) {
    event.preventDefault();

    const code = this.input?.value.trim();
    if (!code) {
      this.setStatus('Enter a promo code.', true);
      this.input?.focus();
      return;
    }

    this.setApplyLoading(true);
    this.setStatus('Applying promo code…');

    try {
      const cart = await this.updateDiscounts(code);
      const applied = this.isCodeApplied(cart, code);

      this.renderCart(cart, {
        openPromo: true,
        inputValue: applied ? '' : code,
        message: applied
          ? 'Promo code applied.'
          : 'This code could not be applied. Check the code and its eligibility.',
        isError: !applied,
      });
    } catch (error) {
      this.setStatus(error.message || 'The promo code could not be applied.', true);
      this.setApplyLoading(false);
    }
  }

  async onRemove(event) {
    const button = event.currentTarget;
    const code = button.dataset.discountCode?.trim();
    if (!code) return;

    const remainingCodes = [...this.querySelectorAll('[data-remove-discount]')]
      .map((removeButton) => removeButton.dataset.discountCode?.trim())
      .filter((appliedCode) => appliedCode && appliedCode.toLocaleLowerCase() !== code.toLocaleLowerCase());

    button.disabled = true;
    button.textContent = 'Removing';
    this.setStatus(`Removing ${code}…`);

    try {
      const cart = await this.updateDiscounts(remainingCodes.join(','));
      const removed = !this.isCodeApplied(cart, code);

      this.renderCart(cart, {
        openPromo: true,
        message: removed ? 'Promo code removed.' : 'The promo code could not be removed.',
        isError: !removed,
      });
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Remove';
      this.setStatus(error.message || 'The promo code could not be removed.', true);
    }
  }
}

if (!customElements.get('blako-cart-discount')) {
  customElements.define('blako-cart-discount', BlakoCartDiscount);
}
